import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CesButtonTertiaryComponent } from '../../../../../ui/ces-buttons/ces-button-tertiary.component';
import { CesDialogFooterComponent } from '../../../../../ui/dialog-footer/dialog-footer.component';
import { isTextEntryEvent } from '../../../../../ui/grid-clipboard/grid-clipboard.utils';

export type ExportFormat = 'png' | 'svg' | 'pdf';
export type ExportOrientation = 'landscape' | 'portrait';
export type ExportAction = 'download' | 'clipboard';

export interface ExportGraphResult {
  format: ExportFormat;
  includeTitle: boolean;
  includeLegend: boolean;
  orientation: ExportOrientation;
  action: ExportAction;
  headerText: string;
}

@Component({
  selector: 'app-export-graph-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslocoModule,
    RadioButtonModule,
    CheckboxModule,
    InputTextModule,
    CesButtonTertiaryComponent,
    CesDialogFooterComponent,
  ],
  host: {
    '(window:keydown.control.c)': 'handleKeyboardCopy($event)',
  },
  templateUrl: './export-graph-dialog.component.html',
})
export class ExportGraphDialogComponent {
  private readonly ref = inject(DynamicDialogRef);

  readonly selectedFormat = model<ExportFormat>('png');
  readonly selectedOrientation = model<ExportOrientation>('landscape');
  readonly includeTitle = model(true);
  readonly includeLegend = model(true);
  readonly headerText = model('');
  readonly isCopying = signal(false);

  readonly showOrientation = computed(() => this.selectedFormat() === 'pdf');

  handleKeyboardCopy(event: KeyboardEvent): void {
    // Ctrl+C inside the header-text input copies the selected text, not the chart.
    if (isTextEntryEvent(event)) return;
    event.preventDefault();
    this.copyToClipboard();
  }

  cancel(): void {
    this.ref.close();
  }

  export(): void {
    this.ref.close(this.buildResult('download'));
  }

  copyToClipboard(): void {
    if (this.isCopying()) return;
    this.isCopying.set(true);
    setTimeout(() => {
      this.ref.close(this.buildResult('clipboard'));
    }, 100);
  }

  private buildResult(action: ExportAction): ExportGraphResult {
    return {
      format: this.selectedFormat(),
      includeTitle: this.includeTitle(),
      includeLegend: this.includeLegend(),
      orientation: this.selectedOrientation(),
      headerText: this.headerText(),
      action,
    };
  }
}
